import React, { useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { ArrowRight, Sparkles, Undo2, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { saveUiPreferences, mergeUiPreferences } from '../../utils/api';
import { markShellEntrance } from '../../utils/uiPreferences';
import { resetGridDensity } from '../../modules/forecast-v3/hooks/useGridDensity';
import { playNewLookTransition } from './newLookTransition';
import { queueNewLookTour } from '../NewLookTour/NewLookTour';
import './NewLookPrompt.css';

// Stored on the user doc (uiPreferences, projected whole by initData) so a
// dismissal follows the viewer across devices. The localStorage copy makes the
// banner and chip react at once and covers a failed save; the two components
// share it through this tiny store.
const DISMISS_KEY = 'newLookPromptDismissedAt';
// Set when the banner switches the viewer; the New look offers a way back
// until RETURN_WINDOW_MS has passed (or the viewer closes the offer).
const SWITCHED_KEY = 'newLookSwitchedAt';
const RETURN_HIDDEN_KEY = 'newLookReturnDismissedAt';
const RETURN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
// Same set Settings → Appearance clears when going back to Classic
const NEW_LOOK_PREFERENCE_KEYS = ['sidebarTheme', 'sidebarCustomColor', 'panelTheme', 'panelCustomColor', 'chartPalette', 'chartCustomColor', 'forecastDensity'];
const LOCAL_DISMISS_KEY = 'lodgeic:newLookPromptDismissed';
// PostHog flag that launches the dashboard invitation (banner + chip). Hidden
// while flags load. Settings → Appearance and the Switch back link ignore it.
const PROMPT_FLAG = 'new-look-prompt';
const listeners = new Set();

function readLocal() {
  try {
    const v = localStorage.getItem(LOCAL_DISMISS_KEY);
    return v === null ? null : v === '1';
  } catch (e) {
    return null;
  }
}
function writeLocal(dismissed) {
  try { localStorage.setItem(LOCAL_DISMISS_KEY, dismissed ? '1' : '0'); } catch (e) { /* private mode */ }
  listeners.forEach((fn) => fn());
}
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

/**
 * Classic-only invitation to the New look: full banner until dismissed, then
 * a small chip that brings the banner back. Returns null state under the New look.
 */
function useNewLookPrompt() {
  const { uiShell, setUiShell, resetNewLookPreferences } = useTheme();
  const navigate = useNavigate();
  const { userSettings, updateSettings } = useUserSettingsContext();
  const local = useSyncExternalStore(subscribe, readLocal);
  const [switching, setSwitching] = useState(false);
  const promptEnabled = useFeatureFlagEnabled(PROMPT_FLAG) === true;
  // A choice made in this browser wins; otherwise the user doc decides.
  const dismissed = local ?? Boolean(userSettings?.uiPreferences?.[DISMISS_KEY]);

  const persist = async (patch) => {
    if (!userSettings?.email) return;
    try {
      await saveUiPreferences(userSettings.email, patch);
      await updateSettings({ uiPreferences: mergeUiPreferences(userSettings.uiPreferences, patch) });
    } catch (e) {
      console.error('Error saving new-look preference:', e);
    }
  };

  return {
    active: promptEnabled && uiShell === 'classic',
    dismissed,
    switching,
    dismiss: () => { writeLocal(true); persist({ [DISMISS_KEY]: new Date().toISOString() }); },
    restore: () => { writeLocal(false); persist({ [DISMISS_KEY]: null }); },
    // Morph the banner's miniature into the shell, then land on the Overview
    // dashboard where the guided tour starts (it ends on Appearance).
    switchToNewLook: (miniature) => {
      setSwitching(true);
      playNewLookTransition(miniature, () => {
        markShellEntrance();
        queueNewLookTour();
        setUiShell('inset');
        navigate('/dashboard/overview');
        persist({ shell: 'inset', [SWITCHED_KEY]: new Date().toISOString(), [RETURN_HIDDEN_KEY]: null });
      });
    },
    prefs: userSettings?.uiPreferences || {},
    hideReturn: () => persist({ [RETURN_HIDDEN_KEY]: new Date().toISOString() }),
    // Back to Classic counts as having seen the invitation: the chip, not the banner.
    switchBack: () => {
      writeLocal(true);
      setUiShell('classic');
      resetNewLookPreferences();
      resetGridDensity();
      persist({
        shell: 'classic',
        [SWITCHED_KEY]: null,
        [DISMISS_KEY]: new Date().toISOString(),
        ...Object.fromEntries(NEW_LOOK_PREFERENCE_KEYS.map((key) => [key, null])),
      });
    },
    uiShell,
  };
}

/** Full banner on the Classic dashboard; hidden once dismissed. */
export default function NewLookPrompt() {
  const { active, dismissed, switching, dismiss, switchToNewLook } = useNewLookPrompt();
  const railRef = useRef(null);
  const cardRef = useRef(null);
  if (!active || dismissed) return null;

  return (
    <aside className="new-look-prompt" aria-label="Try the new look">
      <div className="new-look-prompt-art" aria-hidden="true">
        <span className="new-look-prompt-rail" ref={railRef} />
        <span className="new-look-prompt-card" ref={cardRef}>
          <span /><span /><span />
        </span>
      </div>
      <div className="new-look-prompt-copy">
        <div className="new-look-prompt-eyebrow">New look</div>
        <p className="new-look-prompt-title">The new reports are built for the new look</p>
        <p className="new-look-prompt-body">
          A sidebar for navigation, a cleaner report toolbar and your own colours. You can switch back any time in
          Settings → Appearance.
        </p>
      </div>
      <div className="new-look-prompt-actions">
        <button
          type="button"
          className="new-look-prompt-primary"
          onClick={() => switchToNewLook({ rail: railRef.current, card: cardRef.current })}
          disabled={switching}
        >
          Switch to the new look
          <ArrowRight size={14} aria-hidden="true" />
        </button>
        <button type="button" className="new-look-prompt-secondary" onClick={dismiss}>
          Not now
        </button>
      </div>
      <button type="button" className="new-look-prompt-close" onClick={dismiss} aria-label="Dismiss">
        <X size={14} aria-hidden="true" />
      </button>
    </aside>
  );
}

/** Small chip beside the page title once the banner is dismissed; brings it back. */
export function NewLookChip() {
  const { active, dismissed, restore } = useNewLookPrompt();
  if (!active || !dismissed) return null;

  return (
    <button type="button" className="new-look-chip" onClick={restore} title="Show the new look invitation again">
      <Sparkles size={12} aria-hidden="true" />
      Try the new look
    </button>
  );
}

/**
 * Under the New look, for a week after the banner switched the viewer: a quiet
 * "Switch back" link at the end of the dashboard tab row (dashboard pages only).
 * Closing it hides it for good; Settings → Appearance always works.
 */
export function NewLookReturnLink() {
  const { uiShell, prefs, hideReturn, switchBack } = useNewLookPrompt();
  const [closed, setClosed] = useState(false);
  const switchedAt = Date.parse(prefs[SWITCHED_KEY] || '');
  const open = uiShell === 'inset' && !closed && !prefs[RETURN_HIDDEN_KEY]
    && Number.isFinite(switchedAt) && Date.now() - switchedAt < RETURN_WINDOW_MS;
  if (!open) return null;

  const daysLeft = Math.max(1, Math.ceil((switchedAt + RETURN_WINDOW_MS - Date.now()) / 86400000));
  return (
    <span className="new-look-return">
      <button type="button" className="new-look-return-back" onClick={switchBack}
        title={`Available here for ${daysLeft} more day${daysLeft === 1 ? '' : 's'}, or any time in Settings → Appearance`}>
        <Undo2 size={12} aria-hidden="true" />
        Back to classic
      </button>
      <button type="button" className="new-look-return-close" aria-label="Keep the new look"
        onClick={() => { setClosed(true); hideReturn(); }}>
        <X size={11} aria-hidden="true" />
      </button>
    </span>
  );
}
